import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoriesAPI } from '../../services/api';
import { 
  ArrowLeft, 
  Image as ImageIcon, 
  Plus, 
  Upload, 
  X, 
  AlertCircle, 
  CheckCircle,
  FolderPlus,
  FileText,
  Info,
  Layers
} from 'lucide-react';
import './css/AddCategory.css';

const AddCategory = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      setError('');
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    if (name.trim().length < 2) {
      setError('Category name must be at least 2 characters');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      let imageBase64 = null;
      if (imageFile) {
        imageBase64 = await convertToBase64(imageFile);
      }
      
      await categoriesAPI.create({ 
        name: name.trim(), 
        description: description.trim() || null, 
        image: imageBase64 
      });
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/admin/categories');
      }, 1500);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add category. Please try again.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/categories');
  };

  return (
    <div className="category-page">
      {/* Sidebar Navigation Indicator */}
      <div className="category-breadcrumb">
        <div className="breadcrumb-container">
          <button 
            className="breadcrumb-back" 
            onClick={handleBack}
            type="button"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <div className="breadcrumb-path">
            <span className="breadcrumb-parent">Categories</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Add New Category</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="category-content">
        {/* Page Header */}
        <div className="category-header">
          <div className="header-icon-box">
            <FolderPlus size={32} strokeWidth={1.5} />
          </div>
          <div className="header-text">
            <h1>Create New Category</h1>
            <p>Add a new product category to organize your inventory and improve navigation</p>
          </div>
        </div>

        {/* Form Container */}
        <div className="category-form-wrapper">
          {/* Alert Messages */}
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={22} strokeWidth={2} />
              <div className="alert-content">
                <strong>Error</strong>
                <span>{error}</span>
              </div>
              <button className="alert-close" onClick={() => setError('')}>
                <X size={18} />
              </button>
            </div>
          )}
          
          {success && (
            <div className="alert alert-success">
              <CheckCircle size={22} strokeWidth={2} />
              <div className="alert-content">
                <strong>Success!</strong>
                <span>Category created successfully. Redirecting...</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="category-form">
            {/* Left Column - Form Fields */}
            <div className="form-main">
              {/* Category Name Card */}
              <div className="form-card">
                <div className="card-header">
                  <div className="card-icon">
                    <Layers size={20} strokeWidth={2} />
                  </div>
                  <h2>Category Details</h2>
                </div>
                
                <div className="card-body">
                  <div className="form-group">
                    <label htmlFor="category-name" className="form-label">
                      Category Name
                      <span className="required-badge">Required</span>
                    </label>
                    <input
                      id="category-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="form-input"
                      placeholder="e.g., Electronics, Fashion, Home & Garden"
                      maxLength={50}
                      required
                    />
                    <div className="input-footer">
                      <span className="input-hint">Choose a clear, descriptive name</span>
                      <span className="char-count">{name.length}/50</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="category-description" className="form-label">
                      Description
                      <span className="optional-badge">Optional</span>
                    </label>
                    <textarea
                      id="category-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="form-textarea"
                      placeholder="Describe what products belong in this category..."
                      rows={5}
                      maxLength={500}
                    />
                    <div className="input-footer">
                      <span className="input-hint">Help customers understand this category</span>
                      <span className="char-count">{description.length}/500</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Image Upload */}
            <div className="form-sidebar">
              {/* Image Upload Card */}
              <div className="form-card">
                <div className="card-header">
                  <div className="card-icon">
                    <ImageIcon size={20} strokeWidth={2} />
                  </div>
                  <h2>Category Image</h2>
                </div>

                <div className="card-body">
                  {!imagePreview ? (
                    <div className="upload-zone">
                      <input
                        id="category-image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="file-input-hidden"
                      />
                      <label htmlFor="category-image" className="upload-label">
                        <div className="upload-icon-wrapper">
                          <Upload size={28} strokeWidth={1.5} />
                        </div>
                        <div className="upload-text">
                          <span className="upload-primary">Click to upload</span>
                          <span className="upload-secondary">or drag and drop</span>
                        </div>
                        <div className="upload-formats">
                          PNG, JPG, GIF • Max 5MB
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="image-preview">
                      <div className="preview-image-container">
                        <img 
                          src={imagePreview} 
                          alt="Category preview" 
                          className="preview-image" 
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="remove-image-btn"
                          aria-label="Remove image"
                        >
                          <X size={16} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div className="preview-info">
                        <ImageIcon size={18} strokeWidth={2} />
                        <div className="preview-details">
                          <span className="preview-name">{imageFile?.name}</span>
                          <span className="preview-size">
                            {(imageFile?.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tips Card */}
              <div className="tips-card">
                <div className="tips-header">
                  <Info size={18} strokeWidth={2} />
                  <span>Quick Tips</span>
                </div>
                <ul className="tips-list">
                  <li>Use high-quality images (min 400x400px)</li>
                  <li>Keep category names concise</li>
                  <li>Add descriptions for better SEO</li>
                </ul>
              </div>
            </div>

            {/* Form Actions - Full Width */}
            <div className="form-actions">
              <button
                type="button"
                onClick={handleBack}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || success}
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    <span>Creating...</span>
                  </>
                ) : success ? (
                  <>
                    <CheckCircle size={20} strokeWidth={2} />
                    <span>Created!</span>
                  </>
                ) : (
                  <>
                    <Plus size={20} strokeWidth={2} />
                    <span>Create Category</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCategory;